import React from 'react';
import { code128Geometry } from '../utils/code128';

/**
 * A REAL, SCANNABLE CODE 128 BARCODE, drawn as inline SVG.
 *
 * SVG rather than a canvas or an image: the same symbol is drawn at 26px in a
 * table row and at 56px on an order's ticket stub, and a `viewBox` scales it
 * crisply at both while a raster does not. It also costs no data URL, no
 * `useEffect`, and no dependency — super-admin-ui has neither `jsbarcode` nor
 * `jspdf`, and `utils/code128.ts` is the hand-rolled encoder both this and the
 * Rollo label printer share.
 *
 * `aria-hidden`, ALWAYS. The value is printed beside every barcode in this app
 * as text, so announcing the bars would read the number twice; the barcode is
 * for a scanner and for recognising a document as an order at a glance, and
 * neither is a job for a screen reader.
 *
 * An empty or unencodable value renders nothing — a short symbol that scans as
 * something other than what is written under it is worse than no symbol.
 */
const Barcode: React.FC<{
  value: string;
  /** Drawn height in px. Width follows the symbol's own module count. */
  height?: number;
  /** Px per module. 1 is fine on screen; a scanner wants 2 or more. */
  moduleWidth?: number;
  className?: string;
  color?: string;
}> = ({ value, height = 32, moduleWidth = 1.4, className = '', color = 'currentColor' }) => {
  const { bars, modules } = React.useMemo(() => code128Geometry(value), [value]);
  if (!modules) return null;

  return (
    <svg
      className={className}
      width={modules * moduleWidth}
      height={height}
      viewBox={`0 0 ${modules} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
      shapeRendering="crispEdges"
    >
      {bars.map((bar) => (
        <rect key={bar.x} x={bar.x} y={0} width={bar.width} height={height} fill={color} />
      ))}
    </svg>
  );
};

export default Barcode;
